/* eslint-disable @typescript-eslint/no-base-to-string */ // Becauase of params.value usage.
import React, { useEffect, useState } from "react";
import {
  DataGridPro,
  useGridApiRef,
  GridToolbarQuickFilter,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridCellParams,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
} from "@mui/x-data-grid-pro";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Tooltip, Box, Button, Paper, ButtonGroup, Stack } from "@mui/material";
import ManageCell from "./ManageCell";
import Confirmation from "./Confirmation";
import Cancelation from "./Cancelation";
import moment from "moment-timezone";
import { LicenseInfo } from "@mui/x-license-pro";
import DifferenceRoundedIcon from "@mui/icons-material/DifferenceRounded";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import { IHotelBookingState, validateBooking } from "src/redux/reducers/HotelPortalReducer";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IHotelBookingModificationChanges } from "@vmosolutions/oc-interfaces";
LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_KEY as string);

function Toggle(props: any): JSX.Element {
  return (
    <>
      {props.row.statusName === "modifications" && (
        <DifferenceRoundedIcon fontSize="small" sx={{ cursor: "pointer" }} />
      )}
    </>
  );
}

function NoRowsPrompt(): JSX.Element {
  return (
    <Stack height="100%" alignItems="center" justifyContent="center">
      No Records
    </Stack>
  );
}

interface IBookingsProps {
  updateFilter: (s: string) => void;
  tableMaxHeight: number;
}

interface ICustomToolbarProps {
  updateFilter: (s: string) => void;
  hotelBookingsCounts: IHotelBookingState["hotelBookingsCounts"];
}

function CustomToolbar({ updateFilter, hotelBookingsCounts }: ICustomToolbarProps): JSX.Element {
  return (
    <GridToolbarContainer sx={{ display: "flex", p: 0, m: 0 }}>
      <Grid sx={{ flexGrow: 2, display: "Flex" }}>
        <GridToolbarQuickFilter variant="outlined" sx={{ flexGrow: 2 }} debounceMs={500} />
      </Grid>
      <Grid sx={{ flexGrow: 2 }}>
        <ButtonGroup>
          <Tooltip title="Unchanged Bookings">
            <Button sx={{ color: "white", backgroundColor: "#000E1E" }} onClick={() => updateFilter("N/A")}>
              {hotelBookingsCounts.noChanges.toLocaleString()} unchanged
            </Button>
          </Tooltip>
          <Tooltip title="Total bookings to remove">
            <Button sx={{ color: "white", backgroundColor: "#5a3f3f" }} onClick={() => updateFilter("Delete")}>
              {hotelBookingsCounts.removals.toLocaleString()} to remove
            </Button>
          </Tooltip>
          <Tooltip title="Total bookings with changes to validate">
            <Button sx={{ color: "white", backgroundColor: "#484129" }} onClick={() => updateFilter("Validate")}>
              {hotelBookingsCounts.modifications.toLocaleString()} to validate
            </Button>
          </Tooltip>
          <Tooltip title="Total Cancelations Remaining">
            <Button
              sx={{
                color: "white",
                backgroundImage:
                  "linear-gradient(45deg, #3b2f33 25%, #4a3239 25%, #4a3239 50%, #3b2f33 50%, #3b2f33 75%, #4a3239 75%, #4a3239 100%)",
                backgroundSize: "56.57px 56.57px",
              }}
              onClick={() => updateFilter("Canceled")}
            >
              {hotelBookingsCounts.canceled.toLocaleString()} canceled
            </Button>
          </Tooltip>
          <Tooltip title="Total Confirmations Remaining">
            <Button sx={{ color: "white", backgroundColor: "#223a1f" }} onClick={() => updateFilter("Confirm")}>
              {hotelBookingsCounts.additions.toLocaleString()} to confirm
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Grid>
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
}

function Bookings(props: IBookingsProps): JSX.Element {
  const { updateFilter, tableMaxHeight } = props;
  const apiRef = useGridApiRef();
  const hotelBookings = useSelector((state: IRootState) => state.hotelBookings.hotelBookings);
  const filter = useSelector((state: IRootState) => state.hotelBookings.filter);
  const timeZone = useSelector((state: IRootState) => state.hotelBookings.timeZone);
  const hotelBookingsCounts = useSelector((state: IRootState) => state.hotelBookings.hotelBookingsCounts);
  moment.tz.setDefault(timeZone);
  const dispatch = useDispatch();
  const handleValidateApproval = (
    handleAll: boolean,
    change: IHotelBookingModificationChanges,
    hotel_id: string
  ): void => {
    const newBookingDetails: any = {};
    if (handleAll) {
      Object.keys(change).map((changeList) => {
        Object.entries(change[changeList].connectedFields).map((innerChangeList) => {
          newBookingDetails[innerChangeList[0]] = innerChangeList[1];
        });
        newBookingDetails._id = change[changeList].hotelBookingId;
      });
    } else {
      Object.entries(change.connectedFields).map((changeList) => {
        newBookingDetails[changeList[0]] = changeList[1];
      });
      newBookingDetails._id = change.hotelBookingId;
    }
    dispatch(
      validateBooking({
        booking: newBookingDetails,
        hotelIDs: [hotel_id],
      })
    );
  };

  const panelContent = React.useCallback((params: any) => {
    return (
      <Paper>
        <Grid container sx={{ p: 2, justifyContent: "center" }}>
          <Grid item xs sx={{ mb: 2 }}>
            <>
              {Object.keys(params.row.changes).length >= 1 && (
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={() => handleValidateApproval(true, params.row.changes, params.row.hotel_id)}
                >
                  Acknowledge All
                </Button>
              )}
            </>
          </Grid>
          {Object.keys(params.row.changes).map((key, idx) => {
            return (
              <Grid container item direction="row" key={idx}>
                <Grid item xs sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <strong>{params.row.changes[key].label}</strong>
                </Grid>
                <Grid item xs sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  {params.row.changes[key].oldValue}
                </Grid>
                <Grid item xs={"auto"} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <KeyboardDoubleArrowRightRoundedIcon fontSize="large" />
                </Grid>
                <Grid item xs sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                  {params.row.changes[key].newValue}
                </Grid>
                <Grid item xs={"auto"}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => handleValidateApproval(false, params.row.changes[key], params.row.hotel_id)}
                  >
                    Acknowledge
                  </Button>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    apiRef.current.setQuickFilterValues([filter]);
  }, [apiRef, filter]);

  useEffect(() => {
    apiRef.current.updateRows(hotelBookings);
  }, [apiRef, hotelBookings]);

  return (
    <Box sx={{ height: `calc(${tableMaxHeight}px + 130px)` }}>
      <DataGridPro
        initialState={{
          columns: {
            columnVisibilityModel: {
              hotelName: true,
              name: true,
              description: true,
              inboundFlightNumber: true,
              outboundFlightNumber: true,
              checkIn: true,
              checkOut: true,
              duration: true,
              confirmationCode: true,
              cancelationCode: true,
              status: false,
              __detail_panel_toggle__: true,
              manage: true,
            },
          },
        }}
        columns={[
          {
            minWidth: 140,
            field: "hotelName",
            headerName: "Hotel",
            type: "string",
            hideable: false,
          },
          {
            minWidth: 140,
            field: "crew1",
            headerName: "Name",
            type: "string",
            hideable: false,
          },
          {
            minWidth: 100,
            field: "description",
            headerName: "Short Code",
            type: "string",
            renderCell: (params: GridCellParams) => (
              <Tooltip title="Click to copy">
                <Box
                  sx={{ textTransform: "uppercase", cursor: "copy" }}
                  onClick={() => {
                    navigator.clipboard.writeText(String(params.value));
                  }}
                >
                  {String(params.value)}
                </Box>
              </Tooltip>
            ),
          },
          {
            minWidth: 100,
            field: "inboundFlightNumber",
            headerName: "# (in)",
            type: "string",
            renderCell: (params: GridCellParams) => (
              <Box sx={{ textTransform: "uppercase" }}>{String(params.value)}</Box>
            ),
          },
          {
            minWidth: 100,
            field: "outboundFlightNumber",
            headerName: "# (out)",
            type: "string",
            renderCell: (params: GridCellParams) => (
              <Box sx={{ textTransform: "uppercase" }}>{String(params.value)}</Box>
            ),
          },
          {
            minWidth: 250,
            field: "checkIn",
            headerName: "Check In",
            type: "dateTime",
            valueGetter: (value: any) => {
              return moment(value).toDate();
            },
            renderCell: (params: GridCellParams) => {
              return (
                <Grid
                  item
                  sx={{ display: "flex", justifyContent: "space-evenly", alignItems: "center", width: "100%" }}
                >
                  <Box sx={{ fontWeight: 900, fontSize: "1.05rem" }}>
                    {moment(params.row.checkInLocal).format("HH:mm")}
                  </Box>
                  <Box sx={{ fontSize: "0.7rem", mx: 0.3 }}>on</Box>
                  <Box sx={{ fontSize: "0.9rem", textTransform: "uppercase" }}>
                    {moment(params.row.checkInLocal).format("MMM-DD-YYYY")}
                  </Box>
                </Grid>
              );
            },
            sortComparator: (v1, v2, param1, param2) =>
              param1.api.getCellValue(param1.id, "checkInUTC") - param2.api.getCellValue(param2.id, "checkInUTC"),
          },
          {
            minWidth: 250,
            field: "checkOut",
            headerName: "Check Out",
            type: "dateTime",
            valueGetter: (params, row) => {
              return moment(row.checkOutLocal).toDate();
            },
            renderCell: (params: GridCellParams) => (
              <Grid item sx={{ display: "flex", justifyContent: "space-evenly", alignItems: "center", width: "100%" }}>
                <Box sx={{ fontWeight: 900, fontSize: "1.05rem" }}>
                  {moment(params.row.checkOutLocal).format("HH:mm")}
                </Box>
                <Box sx={{ fontSize: "0.7rem", mx: 0.3 }}>on</Box>
                <Box sx={{ fontSize: "0.9rem", textTransform: "uppercase" }}>
                  {moment(params.row.checkOutLocal).format("MMM-DD-YYYY")}
                </Box>
              </Grid>
            ),
            sortComparator: (v1, v2, param1, param2) =>
              param1.api.getCellValue(param1.id, "checkOutUTC") - param2.api.getCellValue(param2.id, "checkOutUTC"),
          },
          {
            minWidth: 100,
            field: "duration",
            headerName: "Duration",
            type: "string",
            renderCell: (params: GridCellParams) => {
              const start = moment(params.row.checkInLocal);
              const end = moment(params.row.checkOutLocal);
              const days = end.diff(start, "days");
              const hours = end.subtract(days, "days").diff(start, "hours");
              const mins = end.subtract(hours, "hours").diff(start, "minutes");
              return (
                <Grid item sx={{ display: "flex", justifyContent: "start", alignItems: "center", width: "100%" }}>
                  {days > 0 && <Box sx={{ fontSize: "0.8rem", mr: 0.4 }}>{days}d </Box>}
                  {hours > 0 && <Box sx={{ fontSize: "0.8rem", mr: 0.4 }}>{hours}h </Box>}
                  <Box sx={{ fontSize: "0.8rem" }}>{mins}m</Box>
                </Grid>
              );
            },
          },
          {
            minWidth: 150,
            field: "confirmationCode",
            headerName: "Confirmation",
            type: "string",
            renderCell: (params: GridCellParams) => {
              return <Confirmation UItype="text" rowParams={params.row} />;
            },
          },
          {
            minWidth: 150,
            field: "cancelationCode",
            headerName: "Cancelation",
            type: "string",
            renderCell: (params: GridCellParams) => {
              return <Cancelation UItype="text" rowParams={params.row} />;
            },
          },
          {
            minWidth: 100,
            field: "status",
            headerName: "status",
            type: "string",
          },

          {
            minWidth: 20,
            align: "center",
            headerName: "Validate",
            field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
            renderCell: (params) => <Toggle sx={{ cursor: "pointer" }} {...params} />,
          },
          {
            minWidth: 180,
            field: "manage",
            headerName: "Manage",
            type: "string",
            filterable: false,
            hideable: false,
            renderCell: (params: GridCellParams) => {
              return <ManageCell params={params} />;
            },
          },
        ]}
        apiRef={apiRef}
        rows={hotelBookings}
        checkboxSelection={false}
        density="compact"
        slots={{
          toolbar: () => <CustomToolbar updateFilter={updateFilter} hotelBookingsCounts={hotelBookingsCounts} />,
          noRowsOverlay: NoRowsPrompt,
        }}
        pagination
        getRowClassName={(params) => `datagrid-theme--${params.row.status}`}
        getDetailPanelHeight={() => "auto"}
        getDetailPanelContent={(params) => {
          return panelContent(params);
        }}
        getRowId={(row) => row.id}
        pinnedColumns={{ right: ["manage"] }}
      />
    </Box>
  );
}
export default Bookings;
